import { useCallback, useEffect, useId, useRef } from 'react';

import ArrowLeftIcon from '../../icons/arrow-left';
import ChevronRightIcon from '../../icons/chevron-right';

import './styles/carousel.css';

const PROGRAMMATIC_SETTLE_MS = 700;

function prefersReducedMotion() {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function nearestIndex(track) {
    const slides = [...track.children];
    if (!slides.length) return 0;
    const origin = slides[0].offsetLeft;
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - origin - track.scrollLeft);
        if (distance < bestDistance) {
            best = index;
            bestDistance = distance;
        }
    });
    return best;
}

// Swipeable, snap-aligned carousel. Swiping right-to-left moves focus to the
// next slide; tabs, arrow buttons and the keyboard do the same. The active
// index is controlled by the parent so it can live in the URL.
export default function Carousel({ label, slides, activeIndex, onActiveChange }) {
    const trackRef = useRef(null);
    const targetRef = useRef(null);
    const settleTimer = useRef(null);
    const frame = useRef(null);
    const baseId = useId();

    const scrollToIndex = useCallback((index, { smooth = true } = {}) => {
        const track = trackRef.current;
        const slide = track?.children[index];
        if (!track || !slide) return;
        // While a programmatic scroll is running, intermediate positions must
        // not be reported back, or the URL would bounce between slides.
        targetRef.current = index;
        clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => {
            targetRef.current = null;
        }, PROGRAMMATIC_SETTLE_MS);
        track.scrollTo?.({
            left: slide.offsetLeft - track.children[0].offsetLeft,
            behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto',
        });
    }, []);

    useEffect(() => {
        const track = trackRef.current;
        if (!track || targetRef.current != null) return;
        if (nearestIndex(track) !== activeIndex) scrollToIndex(activeIndex, { smooth: false });
    }, [activeIndex, scrollToIndex]);

    useEffect(() => () => {
        clearTimeout(settleTimer.current);
        cancelAnimationFrame(frame.current);
    }, []);

    const handleScroll = () => {
        cancelAnimationFrame(frame.current);
        frame.current = requestAnimationFrame(() => {
            const track = trackRef.current;
            if (!track) return;
            const index = nearestIndex(track);
            if (targetRef.current != null) {
                if (index === targetRef.current) targetRef.current = null;
                return;
            }
            if (index !== activeIndex) onActiveChange(index);
        });
    };

    const goTo = (index) => {
        const next = Math.max(0, Math.min(slides.length - 1, index));
        if (next !== activeIndex) onActiveChange(next);
        scrollToIndex(next);
    };

    const handleTabKeyDown = (event) => {
        const keys = { ArrowRight: activeIndex + 1, ArrowLeft: activeIndex - 1, Home: 0, End: slides.length - 1 };
        if (!Object.hasOwn(keys, event.key)) return;
        event.preventDefault();
        const next = Math.max(0, Math.min(slides.length - 1, keys[event.key]));
        goTo(next);
        event.currentTarget.parentElement?.children[next]?.focus();
    };

    return (
        <section className="carousel" aria-roledescription="carrusel" aria-label={label} style={{ '--carousel-count': slides.length }}>
            <div className="carousel__bar">
                <div className="carousel__tabs" role="tablist" aria-label={`${label}: fases`}>
                    {slides.map((slide, index) => {
                        const isActive = index === activeIndex;
                        return (
                            <button
                                key={slide.key}
                                id={`${baseId}-tab-${slide.key}`}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-controls={`${baseId}-slide-${slide.key}`}
                                tabIndex={isActive ? 0 : -1}
                                className={`carousel__tab${isActive ? ' carousel__tab--active' : ''}`}
                                onClick={() => goTo(index)}
                                onKeyDown={handleTabKeyDown}
                            >
                                <span className="carousel__tab-title">{slide.title}</span>
                                {slide.count != null && <span className="carousel__tab-count">{slide.count}</span>}
                            </button>
                        );
                    })}
                </div>
                <div className="carousel__arrows">
                    <button
                        type="button"
                        className="carousel__arrow"
                        onClick={() => goTo(activeIndex - 1)}
                        disabled={activeIndex === 0}
                    >
                        <ArrowLeftIcon size={18} />
                        <span className="visually-hidden">Fase anterior</span>
                    </button>
                    <button
                        type="button"
                        className="carousel__arrow"
                        onClick={() => goTo(activeIndex + 1)}
                        disabled={activeIndex === slides.length - 1}
                    >
                        <ChevronRightIcon size={18} />
                        <span className="visually-hidden">Fase siguiente</span>
                    </button>
                </div>
            </div>


            <div ref={trackRef} className="carousel__track" onScroll={handleScroll}>
                {slides.map((slide, index) => (
                    <div
                        key={slide.key}
                        id={`${baseId}-slide-${slide.key}`}
                        role="tabpanel"
                        aria-roledescription="diapositiva"
                        aria-labelledby={`${baseId}-tab-${slide.key}`}
                        className={`carousel__slide${index === activeIndex ? ' carousel__slide--active' : ''}`}
                    >
                        {slide.content}
                    </div>
                ))}
            </div>
        </section>
    );
}
